import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import DOMAINS from '../../constants/domains';
import DataTableCard from '../../Components/Table/DataTableCard';

export default function RoleList() {
  const navigate = useNavigate();
  const { showToast, showConfirm } = useToast();
  const [activeDomain, setActiveDomain] = useState(DOMAINS[0].code);
  const [roles, setRoles] = useState(null);

  const loadRoles = async (domain) => {
    setRoles(null);
    try {
      const data = await api.getRoles({ domain });
      setRoles(data);
    } catch (err) {
      console.error(err);
      showToast('Gagal memuat daftar role.', 'danger');
    }
  };

  useEffect(() => {
    loadRoles(activeDomain);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDomain]);

  const handleDelete = async (role) => {
    const confirmed = await showConfirm(
      `Hapus role "${role.name}"? Tindakan ini tidak dapat dibatalkan.`,
      'Hapus Role',
      'danger',
    );
    if (!confirmed) return;

    try {
      await api.deleteRole({ id: role.id });
      showToast('Role berhasil dihapus.', 'success');
      loadRoles(activeDomain);
    } catch (err) {
      console.error(err);
      showToast(`Gagal menghapus role: ${err.message || err}`, 'danger');
    }
  };

  return (
    <div className="container-fluid p-0">
      <DataTableCard
        icon="bi-shield-lock-fill"
        title="Roles & Access"
        subtitle="Kelola role dan katalog permission per domain program."
        actions={
          <>
            {/* DOMAIN FILTER -- tambah domain baru (mis. SMS) cukup di constants/domains.js */}
            <div className="d-flex align-items-center">
              <label className="me-2 fw-medium text-muted small mb-0 d-none d-xl-inline">
                Domain:
              </label>
              <select
                className="form-select form-select-sm rounded-pill shadow-sm border-0 bg-light px-3"
                value={activeDomain}
                onChange={(e) => setActiveDomain(e.target.value)}
                style={{ minWidth: '160px', fontSize: '0.8rem' }}
              >
                {DOMAINS.map((d) => (
                  <option key={d.code} value={d.code}>
                    {d.code} — {d.label}
                  </option>
                ))}
              </select>
            </div>
            <Link to="/roles/add" className="btn btn-primary rounded-pill px-3">
              <i className="bi bi-plus-circle me-2"></i>Tambah Role
            </Link>
          </>
        }
      >
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light text-muted small text-uppercase fw-bold">
              <tr>
                <th className="ps-4 py-3 border-0">Role</th>
                <th className="border-0 py-3">Kode</th>
                <th className="border-0 py-3 text-center">Permission</th>
                <th className="border-0 py-3 text-center">Karyawan</th>
                <th className="border-0 py-3 text-end pe-4">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {!roles ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} className="placeholder-glow">
                    <td className="ps-4 py-3"><span className="placeholder col-8 rounded"></span></td>
                    <td className="py-3"><span className="placeholder col-6 rounded"></span></td>
                    <td className="py-3 text-center"><span className="placeholder col-4 rounded"></span></td>
                    <td className="py-3 text-center"><span className="placeholder col-4 rounded"></span></td>
                    <td className="text-end pe-4 py-3"><span className="placeholder col-6 rounded"></span></td>
                  </tr>
                ))
              ) : roles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-5">
                    <div className="text-muted mb-3 opacity-25">
                      <i className="bi bi-shield-x fs-1"></i>
                    </div>
                    <h6 className="fw-bold text-dark mb-1">Belum ada role</h6>
                    <p className="text-muted small mb-0">
                      Belum ada role untuk domain {activeDomain}.
                    </p>
                  </td>
                </tr>
              ) : (
                roles.map((role) => (
                  <tr
                    key={role.id}
                    className="cursor-pointer transition-all"
                    onClick={() => navigate(`/roles/${role.id}`)}
                  >
                    <td className="ps-4 py-3">
                      <div className="fw-bold text-dark">{role.name}</div>
                      {role.description && (
                        <small className="text-muted">{role.description}</small>
                      )}
                    </td>
                    <td className="py-3">
                      <span className="extra-small fw-medium text-muted bg-light px-2 py-1 rounded">
                        {role.code}
                      </span>
                      {role.isSystem && (
                        <span className="badge bg-secondary bg-opacity-10 text-secondary rounded-pill px-3 py-2 ms-2">
                          Sistem
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-center">
                      <span className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-3 py-2">
                        {role.permissionCount}
                      </span>
                    </td>
                    <td className="py-3 text-center">
                      <span className="badge bg-info bg-opacity-10 text-info rounded-pill px-3 py-2">
                        {role.employeeCount}
                      </span>
                    </td>
                    <td className="py-3 pe-4" onClick={(e) => e.stopPropagation()}>
                      <div className="d-flex flex-nowrap justify-content-end gap-1">
                        <Link
                          to={`/roles/${role.id}`}
                          className="btn btn-outline-primary btn-sm rounded-circle flex-shrink-0"
                          style={{ width: '32px', height: '32px' }}
                          title="Kelola Role"
                        >
                          <i className="bi bi-pencil-square"></i>
                        </Link>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm rounded-circle flex-shrink-0"
                          style={{ width: '32px', height: '32px' }}
                          title={role.isSystem ? 'Role bawaan sistem tidak dapat dihapus' : 'Hapus Role'}
                          disabled={role.isSystem || role.employeeCount > 0}
                          onClick={() => handleDelete(role)}
                        >
                          <i className="bi bi-trash3-fill"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </DataTableCard>
    </div>
  );
}
